import React, { useState } from 'react';
import { 
  Users, UserPlus, FileSpreadsheet, Download, Upload, 
  CheckCircle, Search, Filter, Trash2, Eye, Shield, FileText, Lock, AlertTriangle, CheckCircle2 
} from 'lucide-react';

interface Student {
  id: string;
  name: string;
  rollId: string;
  grade: string;
  disabilityCategory: string;
  guardianName: string;
  guardianPhone: string;
  therapist: string;
  iepStatus: 'ACTIVE' | 'PENDING_REVIEW' | 'COMPLETED';
}

const INITIAL_STUDENTS: Student[] = [
  { id: '1', name: 'Master Rahul Sharma', rollId: 'STU-2026-001', grade: 'Class 4-B', disabilityCategory: 'Autism Spectrum (ASD)', guardianName: 'Sunita Sharma', guardianPhone: '+91 98230 11223', therapist: 'Sarah Jenkins (SLP)', iepStatus: 'ACTIVE' },
  { id: '2', name: 'Miss Ananya Kulkarni', rollId: 'STU-2026-002', grade: 'Class 2-A', disabilityCategory: 'Speech & Articulation Impairment', guardianName: 'Vikram Kulkarni', guardianPhone: '+91 98211 44556', therapist: 'Michael Chen (OT)', iepStatus: 'ACTIVE' },
  { id: '3', name: 'Master Aarav Patel', rollId: 'STU-2026-003', grade: 'Class 5-C', disabilityCategory: 'Cerebral Palsy (Motor Skills)', guardianName: 'Rajesh Patel', guardianPhone: '+91 98900 77889', therapist: 'Priya Nair (PT)', iepStatus: 'PENDING_REVIEW' },
];

interface TenantAdminDashboardProps {
  isSubscriptionActive?: boolean;
}

export const TenantAdminDashboard: React.FC<TenantAdminDashboardProps> = ({ isSubscriptionActive = true }) => {
  const [students, setStudents] = useState<Student[]>(INITIAL_STUDENTS);
  const [activeTab, setActiveTab] = useState<'roster' | 'add_single' | 'bulk_upload'>('roster');
  const [searchTerm, setSearchTerm] = useState('');

  // Single Add State
  const [singleName, setSingleName] = useState('');
  const [singleRollId, setSingleRollId] = useState('');
  const [singleGrade, setSingleGrade] = useState('Class 3-A');
  const [singleCategory, setSingleCategory] = useState('Autism Spectrum (ASD)');
  const [singleGuardianName, setSingleGuardianName] = useState('');
  const [singleGuardianPhone, setSingleGuardianPhone] = useState('');
  const [singleTherapist, setSingleTherapist] = useState('Sarah Jenkins (SLP)');

  // Bulk Upload State
  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [parsedPreview, setParsedPreview] = useState<any[]>([]);

  // If subscription is not active, display subscription lock screen
  if (!isSubscriptionActive) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="bg-white rounded-3xl border border-red-200 shadow-2xl p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
            <Lock className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full uppercase tracking-wider border border-red-300">
              Active Subscription Required
            </span>
            <h2 className="text-3xl font-black text-slate-900">Tenant Subscription Inactive</h2>
            <p className="text-slate-600 text-sm max-w-lg mx-auto">
              Your School Administrator workspace requires an active valid subscription granted by the AbilityOS Super Administrator.
            </p>
          </div>

          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 text-left text-xs space-y-2 max-w-md mx-auto">
            <div className="font-bold text-slate-800">How to Activate:</div>
            <div className="text-slate-600">1. Contact your Super Admin at <span className="font-mono text-blue-600">superadmin@bugfreesolution.com</span></div>
            <div className="text-slate-600">2. Ask Super Admin to toggle "Grant Valid Subscription" in the Control Plane</div>
            <div className="text-slate-600">3. Refresh your dashboard to gain instant access to student enrollment</div>
          </div>

          <div className="pt-2">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow"
            >
              Refresh Subscription Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleAddSingleStudent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleName || !singleGuardianName) return;

    const newStudent: Student = {
      id: Date.now().toString(),
      name: singleName,
      rollId: singleRollId || `STU-2026-00${students.length + 1}`,
      grade: singleGrade,
      disabilityCategory: singleCategory,
      guardianName: singleGuardianName,
      guardianPhone: singleGuardianPhone || '+91 98000 00000',
      therapist: singleTherapist,
      iepStatus: 'ACTIVE'
    };

    setStudents([newStudent, ...students]);
    setSingleName('');
    setSingleRollId('');
    setSingleGuardianName('');
    setSingleGuardianPhone('');
    setActiveTab('roster');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);

      // Simulate parsing Excel / CSV rows
      const mockParsed = [
        { name: 'Miss Diya Sengupta', rollId: 'STU-2026-101', grade: 'Class 1-A', category: 'Hearing Impairment', guardian: 'Rohan Sengupta', phone: '+91 98450 11990', therapist: 'Sarah Jenkins (SLP)' },
        { name: 'Master Ishan Verma', rollId: 'STU-2026-102', grade: 'Class 3-B', category: 'Down Syndrome', guardian: 'Meera Verma', phone: '+91 98765 43210', therapist: 'Michael Chen (OT)' },
        { name: 'Master Tanmay Roy', rollId: 'STU-2026-103', grade: 'Class 6-A', category: 'Visual Impairment (Braille)', guardian: 'Kunal Roy', phone: '+91 98112 23344', therapist: 'Priya Nair (PT)' },
      ];
      setParsedPreview(mockParsed);
    }
  };

  const handleConfirmBulkUpload = () => {
    if (parsedPreview.length === 0) return;

    const uploadedStudents: Student[] = parsedPreview.map((item, idx) => ({
      id: (Date.now() + idx).toString(),
      name: item.name,
      rollId: item.rollId,
      grade: item.grade,
      disabilityCategory: item.category,
      guardianName: item.guardian,
      guardianPhone: item.phone,
      therapist: item.therapist,
      iepStatus: 'ACTIVE'
    }));

    setStudents([...uploadedStudents, ...students]);
    setUploadSuccess(`Successfully imported ${parsedPreview.length} students from "${file?.name}"!`);
    setFile(null);
    setParsedPreview([]);
    setActiveTab('roster');

    setTimeout(() => setUploadSuccess(''), 4000);
  };

  const filteredStudents = students.filter((s) => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.rollId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.disabilityCategory.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Active Subscription Validated
            </span>
          </div>
          <h2 className="text-2xl font-black text-slate-900">School Administrator Student Enrolment Hub</h2>
          <p className="text-xs text-slate-500">Manage student profiles, IEP statuses, and batch upload Excel / CSV rosters</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('roster')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
              activeTab === 'roster' ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Student Roster ({students.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('add_single')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
              activeTab === 'add_single' ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Single Student</span>
          </button>

          <button
            onClick={() => setActiveTab('bulk_upload')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
              activeTab === 'bulk_upload' ? 'bg-emerald-600 text-white shadow' : 'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>Bulk Upload Excel</span>
          </button>
        </div>
      </div>

      {uploadSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          {uploadSuccess}
        </div>
      )}

      {/* Roster View */}
      {activeTab === 'roster' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by student name, Roll ID, category..."
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div className="text-xs text-slate-500 font-semibold">
              Showing {filteredStudents.length} of {students.length} students
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-extrabold uppercase text-slate-600">
                  <th className="p-3">Roll ID</th>
                  <th className="p-3">Student Name</th>
                  <th className="p-3">Grade</th>
                  <th className="p-3">Disability Category</th>
                  <th className="p-3">Guardian Info</th>
                  <th className="p-3">Assigned Therapist</th>
                  <th className="p-3">IEP Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredStudents.map((st) => (
                  <tr key={st.id} className="hover:bg-slate-50 transition">
                    <td className="p-3 font-mono font-bold text-slate-700">{st.rollId}</td>
                    <td className="p-3 font-bold text-slate-900">{st.name}</td>
                    <td className="p-3 font-medium text-slate-600">{st.grade}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded font-bold text-[10px]">
                        {st.disabilityCategory}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">
                      <div className="font-bold text-slate-800">{st.guardianName}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{st.guardianPhone}</div>
                    </td>
                    <td className="p-3 text-slate-700 font-medium">{st.therapist}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${
                        st.iepStatus === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {st.iepStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Single Student View */}
      {activeTab === 'add_single' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 max-w-2xl mx-auto space-y-4">
          <h3 className="text-lg font-extrabold text-slate-900 border-b pb-3">Enrol Single Student</h3>
          
          <form onSubmit={handleAddSingleStudent} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Student Full Name *</label>
                <input
                  type="text" required
                  value={singleName} onChange={(e) => setSingleName(e.target.value)}
                  placeholder="Master Aarav Sharma"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Roll / Student ID</label>
                <input
                  type="text"
                  value={singleRollId} onChange={(e) => setSingleRollId(e.target.value)}
                  placeholder="STU-2026-099"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Grade / Classroom</label>
                <input
                  type="text"
                  value={singleGrade} onChange={(e) => setSingleGrade(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Disability Category</label>
                <select
                  value={singleCategory} onChange={(e) => setSingleCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Autism Spectrum (ASD)">Autism Spectrum (ASD)</option>
                  <option value="Speech & Articulation Impairment">Speech & Articulation Impairment</option>
                  <option value="Cerebral Palsy (Motor Skills)">Cerebral Palsy (Motor Skills)</option>
                  <option value="Down Syndrome">Down Syndrome</option>
                  <option value="Hearing Impairment">Hearing Impairment</option>
                  <option value="Visual Impairment (Braille)">Visual Impairment (Braille)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Guardian Name *</label>
                <input
                  type="text" required
                  value={singleGuardianName} onChange={(e) => setSingleGuardianName(e.target.value)}
                  placeholder="Parent / Guardian Name"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Guardian Phone</label>
                <input
                  type="text"
                  value={singleGuardianPhone} onChange={(e) => setSingleGuardianPhone(e.target.value)}
                  placeholder="+91 98000 00000"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="pt-3 border-t flex justify-end gap-3">
              <button type="button" onClick={() => setActiveTab('roster')} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs">Cancel</button>
              <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow">Enrol Student</button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk Upload Excel View */}
      {activeTab === 'bulk_upload' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 max-w-3xl mx-auto space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <h3 className="text-lg font-extrabold text-slate-900">Batch Bulk Student Excel / CSV Importer</h3>
              <p className="text-xs text-slate-500">Upload standard Excel spreadsheet to import multiple student profiles at once</p>
            </div>

            <a
              href="/downloads/abilityos_student_import_template.csv"
              download
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl flex items-center gap-1.5 transition"
            >
              <Download className="w-4 h-4 text-blue-600" />
              <span>Download Excel Template</span>
            </a>
          </div>

          <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center space-y-3 bg-slate-50 hover:bg-slate-100/50 transition">
            <Upload className="w-10 h-10 text-blue-600 mx-auto" />
            <div className="text-sm font-bold text-slate-800">
              Drag & Drop your student Excel / CSV file here
            </div>
            <div className="text-xs text-slate-400">Supports .xlsx, .xls, .csv files</div>

            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
              className="hidden"
              id="excel-file-input"
            />
            <label
              htmlFor="excel-file-input"
              className="inline-block px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow cursor-pointer transition"
            >
              Select Excel File
            </label>
          </div>

          {file && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs font-bold text-blue-900">
                <span>Selected: {file.name}</span>
                <span className="text-emerald-700">{parsedPreview.length} Rows Ready to Import</span>
              </div>

              {parsedPreview.length > 0 && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 p-2.5 font-bold text-xs text-slate-700">Preview Data to Import:</div>
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500">
                      <tr>
                        <th className="p-2">Roll ID</th>
                        <th className="p-2">Name</th>
                        <th className="p-2">Grade</th>
                        <th className="p-2">Disability Category</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {parsedPreview.map((row, idx) => (
                        <tr key={idx}>
                          <td className="p-2 font-mono font-bold">{row.rollId}</td>
                          <td className="p-2 font-bold text-slate-900">{row.name}</td>
                          <td className="p-2 text-slate-600">{row.grade}</td>
                          <td className="p-2 text-blue-700 font-medium">{row.category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button onClick={() => setFile(null)} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs">Clear File</button>
                <button onClick={handleConfirmBulkUpload} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow">Confirm & Import {parsedPreview.length} Students</button>
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
};
